import { useEffect, useState, useCallback } from "react";
import Lenis from "lenis";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { useSiteContent } from "@/context/SiteContent";
import { SeoAnalytics } from "@/components/helios/SeoAnalytics";
import { AnnouncementBanner } from "@/components/helios/AnnouncementBanner";
import { Navbar } from "@/components/helios/Navbar";
import { Hero } from "@/components/helios/Hero";
import { ClientMarquee } from "@/components/helios/ClientMarquee";
import { Ticker } from "@/components/helios/Ticker";
import { Products } from "@/components/helios/Products";
import { OMSection } from "@/components/helios/OMSection";
import { RoiCalculator } from "@/components/helios/RoiCalculator";
import { ImpactCounter } from "@/components/helios/ImpactCounter";
import { Deployments } from "@/components/helios/Deployments";
import { Faq } from "@/components/helios/Faq";
import { Reviews } from "@/components/helios/Reviews";
import { Contact } from "@/components/helios/Contact";
import { Footer } from "@/components/helios/Footer";
import { RfqWizard } from "@/components/helios/RfqWizard";
import { DatasheetModal } from "@/components/helios/DatasheetModal";
import { VideoModal } from "@/components/helios/VideoModal";
import { QuickActions } from "@/components/helios/QuickActions";
import { ytId } from "@/components/helios/icons";

export default function HomePage() {
  const { content, loading } = useSiteContent();
  const [rfq, setRfq] = useState({ open: false, prefill: null });
  const [ds, setDs] = useState({ open: false, model: null });
  const [video, setVideo] = useState({ open: false, video: null });

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const scrollTo = (sel) => { const el = document.querySelector(sel); if (el) el.scrollIntoView({ behavior: "smooth" }); };
  const openRfq = useCallback((prefill = null) => setRfq({ open: true, prefill }), []);
  const openDatasheet = useCallback((model) => setDs({ open: true, model }), []);
  const openVideo = useCallback((v) => setVideo({ open: true, video: v }), []);

  const heroVideo = () => {
    const id = ytId(content?.galleryMedia?.heroVideoUrl);
    const first = content?.galleryMedia?.projects?.[0];
    openVideo({ id: id || ytId(first?.mediaUrl), title: first?.title || "Helios Field Deployment" });
  };

  if (loading && !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-helios-cyan" size={32} />
      </div>
    );
  }

  return (
    <div className="App">
      <SeoAnalytics />
      <AnnouncementBanner />
      <Navbar onQuote={() => openRfq()} />
      <main>
        <Hero onCalc={() => scrollTo("#roi")} onWatch={heroVideo} />
        <ClientMarquee />
        <Ticker />
        <Products onDatasheet={openDatasheet} />
        <OMSection onProposal={() => openRfq({ leadType: "om" })} />
        <RoiCalculator onReport={(prefill) => openRfq(prefill)} />
        <ImpactCounter />
        <Deployments onPlay={(p) => openVideo({ id: ytId(p.mediaUrl), title: p.title })} />
        <Faq />
        <Reviews />
        <Contact onQuote={() => openRfq()} />
      </main>
      <Footer />

      <RfqWizard open={rfq.open} prefill={rfq.prefill} onClose={() => setRfq((s) => ({ ...s, open: false }))} />
      <DatasheetModal open={ds.open} model={ds.model} onClose={() => setDs((s) => ({ ...s, open: false }))} />
      <VideoModal open={video.open} video={video.video} onClose={() => setVideo((s) => ({ ...s, open: false }))} />
      <QuickActions />
      <Toaster position="top-center" richColors />
    </div>
  );
}
